import { Button, Tab } from "@alifd/next";
import { BefGlobalContext, BefMessageButtonType, BefMessageServiceContext, BefMessageType, BefRpcContext, BefUserServiceContext } from "@beflam/bef-core";
import { useContext, useEffect, useRef, useState } from "react";
import saveAs from "file-saver";
import { DropdownButton } from "../dropdown-button";
import { MixIcon } from "../MixIcon";
import { SelectButton } from "./SelecButton";
import { UserListTable } from "./UserListTable";
import { UserDialogManager, DialogType, DialogVisibleState } from "./UserDialogManager";
import { useUserList } from "./useUserList";
import {
    ACTIVE_USER_ACTIONS,
    CONVERT_FIELDS,
    DELETED_USER_ACTIONS,
    EXPORT_COLUMNS,
    MERGE_WHERES_ACTIVE,
    MERGE_WHERES_DELETED,
    RPC_ASSEMBLY,
    RPC_MODULE,
    WHERES_ACTIVE,
    WHERES_DELETED,
} from "./constants";

export function UserList() {
    const befRpc = useContext(BefRpcContext);
    const messageService = useContext(BefMessageServiceContext);
    const sGlobal = useContext(BefGlobalContext);
    const userService = useContext(BefUserServiceContext);
    const [divHeight, setDivHeight] = useState<any>(0);
    const perRef = useRef(null);
    const useListRef1 = useRef<any>(null);
    const [currentTab, setCurrentTab] = useState<any>(1);
    const [dialogVisible, setDialogVisible] = useState<DialogVisibleState>({
        add: false,
        edit: false,
        updatePassword: false,
        display: false,
        excelImport: false,
        userPermission: false,
    });

    const activeList = useUserList({
        initialWheres: WHERES_ACTIVE,
        mergeFilterWheres: MERGE_WHERES_ACTIVE,
    });
    const deletedList = useUserList({
        initialWheres: WHERES_DELETED,
        mergeFilterWheres: MERGE_WHERES_DELETED,
    });

    const closeDialog = (type: DialogType) => {
        setDialogVisible(prev => ({ ...prev, [type]: false }));
    };

    const openDialog = (type: DialogType) => {
        setDialogVisible(prev => ({ ...prev, [type]: true }));
    };

    const confirmAction = async (actionName: string, rpcMethod: string, rpcParams: any, successMsg: string, errorMsg: string) => {
        try {
            let messageResult = await messageService.showMessage({
                mes: `是否确定${actionName}？`,
                type: BefMessageType.communication,
                btntype: BefMessageButtonType.YesAndNo,
            });
            if (messageResult === 1) {
                await befRpc.callRpcAsync("", RPC_ASSEMBLY, RPC_MODULE, rpcMethod, rpcParams);
                messageService.showMessage({ mes: successMsg });
                return true;
            }
            return false;
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, errorMsg));
            return false;
        }
    };

    const deleteData = async (overrideKeys?: any[], overrideRecords?: any[]) => {
        const keys = overrideKeys ?? activeList.state.selectedRowKeys;
        const records = overrideRecords ?? activeList.state.selectRecords;
        if (keys == null || keys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return;
        }
        const userIds = records.map((val: any) => val.UserId).join(",");
        const ok = await confirmAction(
            `删除用户<${userIds}>`,
            "Delete",
            { Users: keys },
            `删除用户<${userIds}>成功`,
            "删除用户"
        );
        if (ok) {
            activeList.refresh();
            deletedList.refresh();
        }
    };

    const deleteRealData = async (overrideKeys?: any[], overrideRecords?: any[]) => {
        const keys = overrideKeys ?? deletedList.state.selectedRowKeys;
        const records = overrideRecords ?? deletedList.state.selectRecords;
        if (keys == null || keys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return;
        }
        const userIds = records.map((val: any) => val.UserId).join(",");
        const ok = await confirmAction(
            `彻底删除用户<${userIds}>`,
            "Purge",
            { Users: keys },
            `彻底删除用户<${userIds}>成功`,
            "彻底删除用户"
        );
        if (ok) {
            deletedList.refresh();
        }
    };

    const backData = async (overrideKeys?: any[], overrideRecords?: any[]) => {
        const keys = overrideKeys ?? deletedList.state.selectedRowKeys;
        const records = overrideRecords ?? deletedList.state.selectRecords;
        if (keys == null || keys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return;
        }
        const userIds = records.map((val: any) => val.UserId).join(",");
        const ok = await confirmAction(
            `恢复用户<${userIds}>`,
            "UnLock",
            { Users: keys },
            `恢复用户<${userIds}>成功`,
            "恢复用户"
        );
        if (ok) {
            deletedList.refresh();
            activeList.refresh();
        }
    };

    const lock = async (overrideKeys?: any[], overrideRecords?: any[]) => {
        const keys = overrideKeys ?? activeList.state.selectedRowKeys;
        const records = overrideRecords ?? activeList.state.selectRecords;
        if (keys == null || keys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return;
        }
        const userIds = records.map((val: any) => val.UserId).join(",");
        const ok = await confirmAction(
            `锁定用户<${userIds}>`,
            "Lock",
            { Users: keys },
            `锁定用户<${userIds}>成功`,
            "锁定用户"
        );
        if (ok) {
            activeList.refresh();
        }
    };

    const unLock = async (overrideKeys?: any[], overrideRecords?: any[]) => {
        const keys = overrideKeys ?? activeList.state.selectedRowKeys;
        const records = overrideRecords ?? activeList.state.selectRecords;
        if (keys == null || keys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return;
        }
        const userIds = records.map((val: any) => val.UserId).join(",");
        const ok = await confirmAction(
            `解锁用户<${userIds}>`,
            "UnLock",
            { Users: keys },
            `解锁用户<${userIds}>成功`,
            "解锁用户"
        );
        if (ok) {
            activeList.refresh();
        }
    };

    const exportData = async () => {
        try {
            let result = await befRpc.callRpcAsync<string>("", RPC_ASSEMBLY, RPC_MODULE, "Export", {
                Wheres: WHERES_ACTIVE,
                columns: EXPORT_COLUMNS,
                convertFields: CONVERT_FIELDS,
            });
            saveAs(`${sGlobal.config.host}/${result}`);
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户导出"));
        }
    };

    const impersonate = (record: any) => {
        userService
            .impersonate({ userId: record.UserId })
            .then(() => {
                messageService
                    .showMessage({ mes: `用户<${record.UserId}>模拟登录成功，点击确定跳转...` })
                    .then(() => {
                        setTimeout(() => {
                            window.location.reload();
                        }, 100);
                    });
            })
            .catch((error: any) => {
                messageService.showMessage(messageService.handleErr(error, "用户模拟登录"));
            });
    };

    const handleRowAction = (actionId: string, value: any, record: any) => {
        switch (actionId) {
            case 'edit': {
                activeList.setSelected([value], [record]);
                openDialog('edit');
                break;
            }
            case 'delete': {
                deleteData([value], [record]);
                break;
            }
            case 'detail': {
                activeList.setSelected([value], [record]);
                openDialog('display');
                break;
            }
            case 'updatePassword': {
                activeList.setSelected([value], [record]);
                openDialog('updatePassword');
                break;
            }
            case 'permission': {
                activeList.setSelected([record.UserId], [record]);
                openDialog('userPermission');
                break;
            }
            case 'back': {
                backData([value], [record]);
                break;
            }
            case 'realdelete': {
                deleteRealData([value], [record]);
                break;
            }
            case 'personate': {
                impersonate(record);
                break;
            }
        }
    };

    const requireSingleSelection = () => {
        if (activeList.state.selectedRowKeys == null || activeList.state.selectedRowKeys.length !== 1) {
            messageService.showMessage({ mes: "请选择且只选择一条数据" });
            return false;
        }
        return true;
    };

    const requireAnySelection = () => {
        if (activeList.state.selectedRowKeys == null || activeList.state.selectedRowKeys.length === 0) {
            messageService.showMessage({ mes: "请选择至少一条数据" });
            return false;
        }
        return true;
    };

    const handleToolbarAction = (args: { id: string }) => {
        switch (args.id) {
            case 'exportData': {
                exportData();
                break;
            }
            case 'importData': {
                openDialog('excelImport');
                break;
            }
            case 'editData': {
                if (requireSingleSelection()) openDialog('edit');
                break;
            }
            case 'deleteData': {
                deleteData();
                break;
            }
            case 'displayData': {
                if (requireSingleSelection()) openDialog('display');
                break;
            }
            case 'lock': {
                lock();
                break;
            }
            case 'unLock': {
                unLock();
                break;
            }
            case 'updatePassword': {
                if (requireSingleSelection()) openDialog('updatePassword');
                break;
            }
            case 'userPermission': {
                if (requireAnySelection()) openDialog('userPermission');
                break;
            }
            case 'refresh': {
                activeList.refresh();
                break;
            }
            case 'realdelete': {
                deleteRealData();
                break;
            }
            case 'back': {
                backData();
                break;
            }
        }
    };

    const handleAdd = async (userRef: any) => {
        try {
            if (!userRef || !userRef.current) throw new Error("系统错误：userRef is null");
            let validateResult = await userRef.current.validate();
            if (validateResult.errors != null) return;
            let data = userRef.current.dataSource;
            await befRpc.callRpcAsync("", RPC_ASSEMBLY, RPC_MODULE, "Create", data);
            messageService.showMessage({ mes: `新增用户<${data.UserId}>成功` });
            closeDialog('add');
            activeList.refresh();
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户新增"));
        }
    };

    const handleEdit = async (userRef: any) => {
        try {
            if (!userRef || !userRef.current) throw new Error("系统错误：userRef is null");
            await userRef.current.save();
            closeDialog('edit');
            activeList.refresh();
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户编辑"));
        }
    };

    const handleUpdatePassword = async (userRef: any) => {
        try {
            if (!userRef || !userRef.current) throw new Error("系统错误：userRef is null");
            let validateResult = await userRef.current.validate();
            if (validateResult.errors != null) return;
            let data = userRef.current.dataSource;
            await befRpc.callRpcAsync("", RPC_ASSEMBLY, RPC_MODULE, "UpdatePassword", data);
            messageService.showMessage({ mes: `用户<${data.UserId}>密码重置成功` });
            closeDialog('updatePassword');
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "重置用户密码"));
        }
    };

    const handleAssignPermission = async (userRef: any) => {
        try {
            if (!userRef || !userRef.current) throw new Error("系统错误：userRef is null");
            await userRef.current.save();
            const userIds = activeList.state.selectRecords.map((val: any) => val.UserId).join(",");
            messageService.showMessage({ mes: `用户<${userIds}>权限配置成功` });
            closeDialog('userPermission');
        } catch (error) {
            messageService.showMessage(messageService.handleErr(error, "用户权限配置"));
        }
    };

    const handleExcelImportClose = () => {
        closeDialog('excelImport');
        activeList.refresh();
    };

    const handleSelectOK = (wheres: any) => {
        if (wheres) {
            activeList.refreshWithFilter(wheres);
        }
    };

    const handleDlSelect = (wheres: any) => {
        if (wheres) {
            deletedList.refreshWithFilter(wheres);
        }
    };

    useEffect(() => {
        activeList.refresh();
        deletedList.refresh();
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { height } = entry.contentRect;
                if (useListRef1.current !== height) {
                    useListRef1.current = height;
                    let _height = height;
                    if (height > 0) {
                        let diff = _height - 265.45 - 2;
                        if (diff > 0) {
                            if (divHeight !== diff.toString() + 'px')
                                setDivHeight(diff.toString() + 'px');
                        } else {
                            setDivHeight('232px');
                        }
                    }
                }
            }
        });

        if (perRef.current) {
            resizeObserver.observe(perRef.current);
        }

        return () => {
            if (perRef.current) {
                resizeObserver.unobserve(perRef.current);
            }
        };
    }, []);

    return (
        <>
            <div className="bef-user bef-user-section padding-24 gap-24-i" style={{ position: 'relative' }}>
                <div className="bef-user-header" style={{ display: 'flex', flexDirection: 'row', gap: 24, height: 'auto' }}>
                    <span className="bef-user-title" style={{ flex: 1 }}>用户列表</span>
                </div>
                <div className="tab-div" style={{ overflow: 'hidden' }} ref={perRef}>
                    <Tab onChange={setCurrentTab} activeKey={currentTab}>
                        <Tab.Item title="所有用户" key="1">
                            <div className='tab-item  flex-1 display-flex-col' style={{ overflow: 'hidden' }}>
                                <div style={{ overflow: 'auto', display: 'flex' }}>
                                    <div className="bef-user-btngroup justify-end flex-1">
                                        <Button className="custom-btn " style={{ color: '#ffffff', backgroundColor: '#0cc763', borderColor: '#0cc763' }} type="normal" size="medium" onClick={() => openDialog('add')}><MixIcon value="1:add" size="xs" />新增</Button>
                                        <Button className="custom-btn " style={{ color: '#ffffff', backgroundColor: '#3B5998', borderColor: '#3B5998' }} type="normal" size="medium" onClick={() => activeList.refresh()}><MixIcon value="1:reload" size="xs" />刷新</Button>
                                        <DropdownButton className="custom-btn " style={{ color: '#ffffff', backgroundColor: '#735DFF', borderColor: '#735DFF' }} type="normal" size="medium" popupTriggerType="click" icon="1:settingsSharp" content="权限配置" itemClick={handleToolbarAction}
                                            items={[
                                                { id: 'lock', icon: '1:lockClosed', children: '锁定' },
                                                { id: 'unLock', icon: '1:lockOpen', children: '解锁' },
                                                { id: 'userPermission', icon: '1:people', children: '配置权限' },
                                            ]} />
                                        <DropdownButton className="custom-btn " style={{ color: '#ffffff', backgroundColor: '#0c9cfc', borderColor: '#0c9cfc' }} type="normal" size="medium" popupTriggerType="click" content="导入/导出" icon="1:chevronDown" itemClick={handleToolbarAction}
                                            items={[
                                                { id: 'exportData', icon: '1:cloudDownload', children: '导出' },
                                                { id: 'importData', icon: '1:cloudUpload', children: '导入' }
                                            ]} />
                                        <DropdownButton className="custom-btn " style={{ color: '#222f36', backgroundColor: '#f7f8fa', borderColor: '#f7f8fa' }} type="normal" size="medium" popupTriggerType="click" icon="1:settingsSharp" title="配置" itemClick={handleToolbarAction}
                                            items={[
                                                { id: 'deleteData', icon: '1:trash', children: '删除' },
                                                { id: 'updatePassword', icon: '1:key', children: '重置密码' }
                                            ]} />
                                        <SelectButton onOk={handleSelectOK} onReset={() => { }} data={null}></SelectButton>
                                    </div>
                                </div>

                                <UserListTable
                                    dataSource={activeList.state.dataSource}
                                    selectedRowKeys={activeList.state.selectedRowKeys}
                                    columnWidth={activeList.state.columnWidth}
                                    total={activeList.state.data.length}
                                    currentPage={activeList.state.currentPage}
                                    divHeight={divHeight}
                                    actionItems={ACTIVE_USER_ACTIONS}
                                    onAction={handleRowAction}
                                    onSelectionChange={activeList.onSelectionChange}
                                    onColumnResize={activeList.onColumnResize}
                                    onPaginationChange={activeList.onPaginationChange}
                                />
                            </div>
                        </Tab.Item>
                        <Tab.Item title="已删除" key="2">
                            <div className='tab-item  flex-1 display-flex-col' style={{ overflow: 'hidden' }}>
                                <div style={{ overflow: 'auto', display: 'flex' }}>
                                    <div className="bef-user-btngroup justify-end flex-1">
                                        <Button className="custom-btn " style={{ color: '#ffffff', backgroundColor: '#3B5998', borderColor: '#3B5998' }} type="normal" size="medium" onClick={() => deletedList.refresh()}><MixIcon value="1:reload" size="xs" />刷新</Button>
                                        <DropdownButton className="custom-btn " style={{ color: '#222f36', backgroundColor: '#f7f8fa', borderColor: '#f7f8fa' }} type="normal" size="medium" popupTriggerType="click" icon="1:settingsSharp" title="配置" itemClick={handleToolbarAction}
                                            items={[
                                                { id: 'realdelete', icon: '1:trash', children: '彻底删除' },
                                                { id: 'back', icon: '1:arrowUndo', children: '恢复' },
                                            ]} />
                                        <SelectButton onOk={handleDlSelect} onReset={() => { }} data={null} needState={false}></SelectButton>
                                    </div>
                                </div>

                                <UserListTable
                                    dataSource={deletedList.state.dataSource}
                                    selectedRowKeys={deletedList.state.selectedRowKeys}
                                    columnWidth={deletedList.state.columnWidth}
                                    total={deletedList.state.data.length}
                                    currentPage={deletedList.state.currentPage}
                                    divHeight={divHeight}
                                    actionItems={DELETED_USER_ACTIONS}
                                    onAction={handleRowAction}
                                    onSelectionChange={deletedList.onSelectionChange}
                                    onColumnResize={deletedList.onColumnResize}
                                    onPaginationChange={deletedList.onPaginationChange}
                                />
                            </div>
                        </Tab.Item>
                    </Tab>
                </div>
            </div>

            <UserDialogManager
                visible={dialogVisible}
                selectedUserId={activeList.state.selectedRowKeys.length > 0 ? activeList.state.selectedRowKeys[0] : null}
                selectedUserIds={activeList.state.selectedRowKeys}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onUpdatePassword={handleUpdatePassword}
                onAssignPermission={handleAssignPermission}
                onClose={closeDialog}
                onExcelImportClose={handleExcelImportClose}
            />
        </>
    );
}
